import { z } from 'zod';
import { SensorType, AnomalyLevel, SENSOR_CONFIGS } from '../types/sensor.js';
// ---------------------------------------------------------------------------
// 기본 상수 스키마
// ---------------------------------------------------------------------------
export const SensorTypeSchema = z.enum([
    SensorType.TEMPERATURE,
    SensorType.HUMIDITY,
    SensorType.ILLUMINANCE,
    SensorType.CO2,
    SensorType.ATMOSPHERIC_PRESSURE,
    SensorType.UV_INDEX,
    SensorType.WIND_SPEED,
    SensorType.WIND_DIRECTION,
    SensorType.RAINFALL,
    SensorType.SOIL_MOISTURE,
    SensorType.SOIL_TEMPERATURE,
    SensorType.EC,
    SensorType.PH,
    SensorType.LEAF_WETNESS,
]);
export const AnomalyLevelSchema = z.enum([
    AnomalyLevel.NORMAL,
    AnomalyLevel.WARNING,
    AnomalyLevel.CRITICAL,
]);
// ---------------------------------------------------------------------------
// 센서별 값 범위 검증 스키마 (런타임 이상치 탐지)
// ---------------------------------------------------------------------------
/**
 * 센서 타입에 맞는 값 범위 검증기를 반환한다.
 * MQTT 수신 시 유효 범위 밖 값을 즉시 거부한다.
 */
export function buildSensorValueSchema(sensorType) {
    const { validMin, validMax } = SENSOR_CONFIGS[sensorType];
    return z
        .number()
        .min(validMin, { message: `${sensorType} 값은 최솟값 ${validMin} 이상이어야 합니다` })
        .max(validMax, { message: `${sensorType} 값은 최댓값 ${validMax} 이하여야 합니다` });
}
// ---------------------------------------------------------------------------
// MQTT 센서 번들 페이로드 스키마 — 장비당 단일 JSON
// 토픽: grosync/{deviceId}/data
// ---------------------------------------------------------------------------
/**
 * 엔벨로프(ts/readings 형태) 검증용 스키마.
 *
 * 값의 센서별 유효 범위 검증은 여기서 하지 않는다 — 핸들러가 reading 단위로
 * `buildSensorValueSchema()`를 적용해, 일부 reading이 범위를 벗어나도 나머지는
 * 정상 저장하고 해당 reading만 DLQ로 격리한다.
 */
export const SensorBundleSchema = z.object({
    ts: z.string().datetime({ message: 'ISO 8601 형식이어야 합니다' }),
    readings: z
        .record(SensorTypeSchema, z.number().finite())
        .refine((r) => Object.keys(r).length > 0, { message: 'readings는 비어 있을 수 없습니다' }),
    quality: z.record(SensorTypeSchema, z.number().int().min(0).max(100)).optional(),
});
// ---------------------------------------------------------------------------
// MQTT 구동기 노드 상태 페이로드 스키마 (KS X 3267 부속서 A.2)
// 토픽: grosync/{masterId}/node/{slaveAddr}/actuator
// 페이로드: { ts, ch: { "<채널0base>": { st, rem } } }
//   st  = KS X 3267 상태 코드(부속서 B.2)
//   rem = 남은 동작시간(초)
// ---------------------------------------------------------------------------
export const ActuatorChannelStatusSchema = z.object({
    st: z.number().int().min(0), // 상태 코드
    rem: z.number().int().min(0), // 남은 동작시간(초)
});
export const ActuatorStatusSchema = z.object({
    ts: z.string().datetime({ message: 'ISO 8601 형식이어야 합니다' }),
    ch: z.record(z.string(), ActuatorChannelStatusSchema),
});
// ---------------------------------------------------------------------------
// DB 레코드 스키마
// ---------------------------------------------------------------------------
export const SensorReadingSchema = z.object({
    id: z.string().uuid(),
    deviceId: z.string().min(1),
    sensorType: SensorTypeSchema,
    value: z.number().finite(),
    unit: z.string().min(1),
    timestamp: z.string().datetime(),
    anomaly: AnomalyLevelSchema,
    quality: z.number().int().min(0).max(100).nullable(),
});
// ---------------------------------------------------------------------------
// 조회 파라미터 스키마
// ---------------------------------------------------------------------------
export const SensorQueryParamsSchema = z.object({
    deviceId: z.string().min(1),
    sensorType: SensorTypeSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
//# sourceMappingURL=sensor.js.map